// Config
import { appEnvironment, AppEnvironment } from "../config/appEnv";

// Interfaces
import { CoachBillingInterval, CoachPlanKey } from "../interfaces/Coach";
import { StripePrice } from "../interfaces/Stripe/StripePrice";

/**
 * Canonical subscription-plan catalog for all self-serve Stripe tiers.
 *
 * This file is the app-level source of truth for:
 * - plan identity
 * - UI-facing plan metadata
 * - hardcoded pricing values used by the fallback path
 * - environment-specific Stripe price IDs
 *
 * The rest of the app should consume the helpers exported here rather than
 * duplicating plan definitions or reading tier price IDs directly from config.
 */
export type SelfServeSubscriptionPlanKey = Exclude<CoachPlanKey, "enterprise">;

// Shared, interval-agnostic metadata used across signup, account, and upgrade UI.
type SubscriptionPlanMetadata = {
    name: string;
    seatLimit: number;
    seatLabel: string;
    description: string;
    accent: string;
    eyebrow: string;
    featured?: boolean;
    trialLabel: string;
};

// Static pricing values controlled by the app for display and fallback pricing.
type SubscriptionPlanPricing = {
    month: number;
    year: number;
};

// Fully resolved plan record used by runtime consumers and lookup helpers.
export type SubscriptionPlanDefinition = SubscriptionPlanMetadata & {
    planKey: SelfServeSubscriptionPlanKey;
    interval: CoachBillingInterval;
    priceId: string;
    unitAmount: number;
    currency: string;
    intervalCount: number;
};

type CreateSubscriptionPlanCatalogOptions = {
    appEnvironment: AppEnvironment;
    env?: Record<string, string | undefined>;
};

// ============================================================================
// Plan Metadata
// ============================================================================

const subscriptionPlanMetadata: Record<SelfServeSubscriptionPlanKey, SubscriptionPlanMetadata> = {
    tier_1: {
        name: "Starter",
        seatLimit: 3,
        seatLabel: "Up to 3 clients",
        description: "Best for solo practitioners who want a longer on-ramp before recurring billing starts.",
        accent: "#6F8FD9",
        eyebrow: "For solo practices",
        trialLabel: "2-month free trial",
    },
    tier_2: {
        name: "Growth",
        seatLimit: 10,
        seatLabel: "Up to 10 clients",
        description: "More room to add clients as your practice grows and your workflow expands.",
        accent: "#5F9D86",
        eyebrow: "Most popular",
        featured: true,
        trialLabel: "14-day free trial",
    },
    tier_3: {
        name: "Pro",
        seatLimit: 25,
        seatLabel: "Up to 25 clients",
        description: "Built for established teams that need larger capacity without a custom contract.",
        accent: "#C58D57",
        eyebrow: "For established teams",
        trialLabel: "14-day free trial",
    },
};

// ============================================================================
// Plan Pricing
// ============================================================================

const subscriptionPlanPricing: Record<SelfServeSubscriptionPlanKey, SubscriptionPlanPricing> = {
    tier_1: {
        month: 999,
        year: 9999,
    },
    tier_2: {
        month: 2500,
        year: 24000,
    },
    tier_3: {
        month: 3000,
        year: 30000,
    },
};

const selfServePlanKeys = Object.keys(subscriptionPlanMetadata) as SelfServeSubscriptionPlanKey[];
const billingIntervals: CoachBillingInterval[] = ["month", "year"];

// ============================================================================
// Env Resolution
// ============================================================================

const toTierIndex = (planKey: SelfServeSubscriptionPlanKey): "1" | "2" | "3" => {
    switch (planKey) {
        case "tier_1":
            return "1";
        case "tier_2":
            return "2";
        case "tier_3":
            return "3";
    }
};

const getTierPriceIdEnvVarName = (environment: AppEnvironment, planKey: SelfServeSubscriptionPlanKey, interval: CoachBillingInterval): string => {
    const envPrefix = environment === "production" ? "REACT_APP_STRIPE" : "REACT_APP_STRIPE_DEV";
    const tierIndex = toTierIndex(planKey);
    const intervalKey = interval === "month" ? "MONTH" : "YEAR";

    return `${envPrefix}_TIER_${tierIndex}_${intervalKey}_PRICE_ID`;
};

const requirePriceId = (
    env: Record<string, string | undefined>,
    environment: AppEnvironment,
    planKey: SelfServeSubscriptionPlanKey,
    interval: CoachBillingInterval
): string => {
    const envVarName = getTierPriceIdEnvVarName(environment, planKey, interval);
    const value = env[envVarName]?.trim();

    if (!value) {
        throw new Error(`${envVarName} is required to configure subscription plan pricing for ${environment}.`);
    }

    return value;
};

// ============================================================================
// Catalog Construction
// ============================================================================

export const createSubscriptionPlanCatalog = ({
    appEnvironment: currentEnvironment,
    env = process.env,
}: CreateSubscriptionPlanCatalogOptions): SubscriptionPlanDefinition[] =>
    selfServePlanKeys.flatMap(planKey =>
        billingIntervals.map(interval => ({
            ...subscriptionPlanMetadata[planKey],
            planKey,
            interval,
            priceId: requirePriceId(env, currentEnvironment, planKey, interval),
            unitAmount: subscriptionPlanPricing[planKey][interval],
            currency: "usd",
            intervalCount: 1,
        }))
    );

// Resolve the active-environment plan catalog once so every consumer reads the
// same normalized plan definitions during the app session.
const subscriptionPlanCatalog = createSubscriptionPlanCatalog({ appEnvironment });

// ============================================================================
// Public Accessors
// ============================================================================

export const getSubscriptionPlans = (): SubscriptionPlanDefinition[] => [...subscriptionPlanCatalog];

export const getSubscriptionPlan = (planKey: SelfServeSubscriptionPlanKey, interval: CoachBillingInterval): SubscriptionPlanDefinition | undefined =>
    subscriptionPlanCatalog.find(plan => plan.planKey === planKey && plan.interval === interval);

export const getSubscriptionPlanByPriceId = (priceId?: string): SubscriptionPlanDefinition | undefined =>
    subscriptionPlanCatalog.find(plan => plan.priceId === priceId);

export const getSubscriptionStripePrices = (): StripePrice[] =>
    subscriptionPlanCatalog.map(plan => ({
        priceId: plan.priceId,
        unitAmount: plan.unitAmount,
        currency: plan.currency,
        interval: plan.interval,
        intervalCount: plan.intervalCount,
    }));

// Interval-agnostic summaries used by plan-card style UIs.
export const getSubscriptionPlanSummaries = (): Array<SubscriptionPlanMetadata & { planKey: SelfServeSubscriptionPlanKey }> =>
    selfServePlanKeys.map(planKey => ({
        planKey,
        ...subscriptionPlanMetadata[planKey],
    }));
